// src/services/ai.js — the browser's half of Allo AI, in a chat.
//
// 🟥 THE ASSISTANT WAS APP ONLY, AND THE WEB CLIENT WAS BUILT TO HIDE IT. Not
//    for a good reason — the browser can call the same route the phone calls —
//    but because nothing here had ever been written. So `@ai` typed on the web
//    was three literal characters in a message, and an answer that arrived from
//    the phone rendered as an ordinary line of text with no sign of where it
//    came from.
//
// 🟢 THIS IS THE SAME MECHANISM, NOT A SECOND ONE. The same backend route, the
//    same field on the same message, the same marker on the same chat document.
//    A question asked in either client is answered the same way and looks the
//    same in both.
//
// 🟥 AND THE ANSWER GOES THROUGH THE ORDINARY SEND PATH, WHICH SEALS IT. The
//    phone writes its AI replies as plain `text` with no per-device keys, so
//    Allo AI's answer is the one message in a conversation that anybody with
//    database access can read — while the question that prompted it is sealed.
//    Nothing here repeats that: an answer is a message, and messages in this
//    client are sealed like every other one.

import { BACKEND_URL } from './socket';

/**
 * Ask the assistant.
 *
 * 🟢 FAIL SOFT, LIKE THE PHONE. A refused request, a 500 or a dead network
 *    comes back as a sentence rather than as an exception, because the caller's
 *    job at that point is to take the thinking row down and say something —
 *    not to decide what a network error means.
 */
export async function askAllo(text, { history = [], userName, people, memory, signal } = {}) {
  try {
    const r = await fetch(`${BACKEND_URL}/api/gemini-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, history, userName, people, memory }),
      signal,
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.error || 'ai_failed');
    return { ok: true, content: String(data?.content || '').trim() };
  } catch (e) {
    if (e?.name === 'AbortError') return { ok: false, aborted: true, content: '' };
    return { ok: false, content: '' };
  }
}

/* ═══ THE MENTION ══════════════════════════════════════════════════════════
   🟢 ONE DEFINITION OF WHAT COUNTS AS ASKING, used by the composer to arm the
      card and by the send path to decide whether to answer. Two regexes in two
      places is how a message gets the card and no answer, or an answer and no
      card. */

/** The trigger, as typed: `@ai` at the end of what has been written so far. */
export const AI_TRIGGER = /(^|\s)@ai$/i;

/** The mention, anywhere in a finished message. */
export const AI_MENTION = /(^|\s)@ai\b/i;

export const isAiAsk = (text) => AI_MENTION.test(String(text || ''));

/** What to send the model: the message without the mention that summoned it. */
export const askText = (text) => String(text || '').replace(/@ai/ig, '').trim();

/**
 * 🟥 THE PHONE STRIPS THE TAG OUT OF THE INPUT AND PUTS IT BACK AT SEND TIME,
 *    so the stored message reads "@ai what is the weather" and the mention is
 *    there to render. Doing it differently here would mean a message sent from
 *    the browser had no mention in it to draw, and the same conversation would
 *    look different depending on which client wrote the line.
 */
export const withMention = (text) => (isAiAsk(text) ? String(text) : `@ai ${String(text || '').trim()}`.trim());
